import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AppLayout from './components/AppLayout';
import { useFeatureFlag } from './lib/featureFlags';
import UsersPage     from './pages/UsersPage';
import AppRolesPage  from './pages/AppRolesPage';
import ContactsPage  from './pages/ContactsPage';
import AdminSetupPage from './pages/AdminSetupPage';
import AuditLogPage   from './pages/AuditLogPage';
import MyTestPage    from './pages/MyTestPage';
import LoginPage     from './pages/LoginPage';

// Org canvas pulls in @xyflow/react + elkjs + framer-motion (~1.8 MB). Lazy-load
// so admins only pay the cost when they navigate to /org.
const OrganizationPage = React.lazy(() => import('./pages/OrganizationPage'));

// Public routes — accessible WITHOUT being signed in as admin. Used for
// userless surfaces (Phase 6 smoke test, Phase 11 EPR /my-reviews, etc.)
// where the user authenticates via SMS/email OTP from inside the page itself.
const PUBLIC_ROUTE_PREFIXES = ['/my-test', '/my-reviews', '/my-feedback', '/my-cascade'];

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

const AppRoutes: React.FC = () => {
  const { loading, isAdmin, firebaseUser } = useAuth();
  const contactsEnabled       = useFeatureFlag('contacts.enabled');
  const useContactsForLogin   = useFeatureFlag('contacts.useContactsForLogin');
  const location              = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-gray-200 border-t-[#011132] rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  // ── Public routes (no admin auth required) ──────────────────────────────
  // Rendered standalone (no AppLayout) since the user may not have a `users/`
  // doc and shouldn't see admin nav.
  if (isPublicRoute(location.pathname)) {
    return (
      <Routes>
        <Route
          path="/my-test"
          element={useContactsForLogin ? <MyTestPage /> : <FlagDisabledNotice flag="contacts.useContactsForLogin" />}
        />
        {/* Phase 11 will add /my-reviews + /my-feedback + /my-cascade routes here */}
        <Route path="*" element={<NotFoundPublic />} />
      </Routes>
    );
  }

  // ── Admin auth gate ─────────────────────────────────────────────────────
  if (!firebaseUser) {
    return <LoginPage />;
  }

  if (firebaseUser && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <span className="material-symbols-rounded text-red-400" style={{ fontSize: 28 }}>block</span>
          </div>
          <h2 className="text-lg font-bold text-gray-800">Access Restricted</h2>
          <p className="text-sm text-gray-500">Account Management requires admin or superadmin access.</p>
          <p className="text-xs text-gray-400">{firebaseUser.email}</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/"            element={<Navigate to="/users" replace />} />
        <Route path="/users"       element={<UsersPage />} />
        <Route path="/roles"       element={<AppRolesPage />} />
        <Route path="/org"         element={
          <Suspense fallback={<div className="h-screen flex items-center justify-center text-sm text-stone-400">Loading organization…</div>}>
            <OrganizationPage />
          </Suspense>
        } />
        <Route
          path="/contacts"
          element={contactsEnabled ? <ContactsPage /> : <Navigate to="/users" replace />}
        />
        <Route path="/admin/setup" element={<AdminSetupPage />} />
        <Route path="/admin/audit" element={<AuditLogPage />} />
        <Route path="*"            element={<Navigate to="/users" replace />} />
      </Routes>
    </AppLayout>
  );
};

const FlagDisabledNotice: React.FC<{ flag: string }> = ({ flag }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="max-w-sm w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center space-y-4">
      <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
        <span className="material-symbols-rounded text-amber-500" style={{ fontSize: 28 }}>toggle_off</span>
      </div>
      <h2 className="text-lg font-bold text-gray-800">Feature Disabled</h2>
      <p className="text-sm text-gray-500">
        Surface gated by feature flag <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{flag}</code>.
        Enable in <a href="/admin/setup" className="text-[#011132] underline">/admin/setup</a> (superadmin).
      </p>
    </div>
  </div>
);

const NotFoundPublic: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="max-w-sm w-full text-center space-y-2">
      <h2 className="text-lg font-bold text-gray-800">Not found</h2>
      <p className="text-sm text-gray-500">No public route matches this URL.</p>
    </div>
  </div>
);

const App: React.FC = () => (
  <AuthProvider>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </AuthProvider>
);

export default App;
